//based on danlines implementation https://github.com/ueberdosis/tiptap/issues/1036

import { Extension } from "@tiptap/core";
import { AllSelection, TextSelection, Transaction } from "prosemirror-state";
import { Node } from "prosemirror-model";
import { CommandProps } from "@tiptap/react";

declare module "@tiptap/core" {
  interface Commands {
    indent: {
      indent: () => (props: CommandProps) => boolean;
      outdent: () => (props: CommandProps) => boolean;
    };
  }
}

const IndentConfig = {
  min: 0,
  max: 300,
  more: 30,
  less: -30,
};

export function isBulletListNode(node: Node) {
  return node.type.name === "bullet_list";
}

export function isOrderedListNode(node: Node) {
  return node.type.name === "order_list";
}

export function isListNode(node: Node) {
  return isBulletListNode(node) || isOrderedListNode(node);
}

function setNodeIndentMarkup(transaction: Transaction, pos: number, delta: number) {
  if (!transaction.doc) {
    return transaction;
  }

  const node = transaction.doc.nodeAt(pos);
  if (!node) {
    return transaction;
  }

  let newIndentation = (node.attrs.indent || 0) + delta;
  newIndentation = Math.max(newIndentation, IndentConfig.min);
  newIndentation = Math.min(newIndentation, IndentConfig.max);

  if (newIndentation === node.attrs.indent) {
    return transaction;
  }

  const nodeAttrs = {
    ...node.attrs,
    indent: newIndentation,
  };

  return transaction.setNodeMarkup(pos, node.type, nodeAttrs, node.marks);
}

const updateIndentLevel = (transaction: Transaction, delta: number) => {
  const { doc, selection } = transaction;

  if (!doc || !selection) {
    return transaction;
  }

  if (!(selection instanceof TextSelection || selection instanceof AllSelection)) {
    return transaction;
  }

  doc.nodesBetween(selection.from, selection.to, (node, pos) => {
    const nodeType = node.type;

    if (nodeType.name === "paragraph" || nodeType.name === "heading") {
      transaction = setNodeIndentMarkup(transaction, pos, delta);
      return false;
    }
    return !isListNode(node);
  });

  return transaction;
};

export const Indent = Extension.create({
  name: "indent",

  addOptions() {
    return {
      types: ["heading", "paragraph"],
      indentLevels: [IndentConfig.min, 30, 60, 90, 120, 150, 180, IndentConfig.max],
      defaultIndentLevel: IndentConfig.min,
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          indent: {
            default: this.options.defaultIndentLevel,
            renderHTML: (attributes) => ({
              style: `margin-left: ${attributes.indent}px!important;`,
            }),
            parseHTML: (element) => parseInt(element.style.marginLeft) || this.options.defaultIndentLevel,
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      indent:
        () =>
        ({ tr: transaction, state, dispatch, editor }: CommandProps) => {
          const { selection } = state;
          transaction = transaction.setSelection(selection);
          transaction = updateIndentLevel(transaction, IndentConfig.more);

          if (transaction.docChanged) {
            dispatch && dispatch(transaction);
            return true;
          }

          editor.chain().focus().run();

          return false;
        },
      outdent:
        () =>
        ({ tr: transaction, state, dispatch, editor }: CommandProps) => {
          const { selection } = state;
          transaction = transaction.setSelection(selection);
          transaction = updateIndentLevel(transaction, IndentConfig.less);

          if (transaction.docChanged) {
            dispatch && dispatch(transaction);
            return true;
          }

          editor.chain().focus().run();

          return false;
        },
    };
  },
  addKeyboardShortcuts() {
    return {
      Tab: () => {
        if (!(this.editor.isActive("bulletList") || this.editor.isActive("orderedList"))) {
          this.editor.commands.indent();
          return true;
        }
        return false;
      },
      "Shift-Tab": () => {
        if (!(this.editor.isActive("bulletList") || this.editor.isActive("orderedList"))) {
          this.editor.commands.outdent();
          return true;
        }
        return false;
      },
    };
  },
});

export default Indent;
